import { useGlobalContext } from '@/context/Global/GlobalContext';
import { useOrderDetailContext } from '@/context/OrderDetail/OrderDetailContext';
import { useAcceptOrder } from '@/hooks/order/details/useAcceptOrder';
import { useCancelOrder } from '@/hooks/order/details/useCancelOrder';
import { useGetDeliveryInfoData } from '@/hooks/order/details/useGetDeliveryInfo';
import { useSendOrderNotification } from '@/hooks/order/details/useSendOrderNotification';
import { useSetAsdeliveredOrder } from '@/hooks/order/details/useSetDelivered';
import { useGetOrderDetails } from '@/hooks/order/useGetOrderDetails';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { orderDetailsResponseToDetailActionsUi } from '../mapper';
import { DetailActionsUi, initialState } from './DetailActionsUi';

export function useDetailActionsViewController() {
  const [uiState, setUiState] = useImmer<DetailActionsUi>(initialState);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const orderDetailId = useParams().orderDetailId ?? '';
  const { data } = useGetOrderDetails(orderDetailId);
  const { session } = useGlobalContext();

  const acceptOrderHook = useAcceptOrder();
  const cancelOrderHook = useCancelOrder();
  const setAsDeliveredHook = useSetAsdeliveredOrder();
  const sendOrderNotificationHook = useSendOrderNotification();

  const orderDetailContext = useOrderDetailContext();

  const { data: deliveryInfoData } = useGetDeliveryInfoData(orderDetailId);

  useEffect(() => {
    if (!data || session === null) return;
    const newUiState = orderDetailsResponseToDetailActionsUi(
      data,
      session,
      deliveryInfoData
    );
    setUiState(newUiState);
  }, [data, session, deliveryInfoData]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onClickedExportOrder = () => {
    if (!data) return;
    const { seller_document, order_detail_number } = data;
    if (!seller_document || !order_detail_number) return;
    const a = document.createElement('a');
    a.href = seller_document;
    a.download = `pedido_${order_detail_number}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    handleMenuClose();
  };

  const onClickedAcceptOrder = () => {
    if (!orderDetailId) return;
    acceptOrderHook.acceptOrder(orderDetailId);
  };

  const onClickedCancelOrder = () => {
    if (!orderDetailId) return;
    cancelOrderHook.cancelOrder({ orderId: orderDetailId, reason: 'reason' });
    handleMenuClose();
  };

  const onClickedMarkAsDelivered = () => {
    if (!orderDetailId) return;
    setAsDeliveredHook.setOrderAsDelivered(orderDetailId);
  };

  const onClickedSendWhatsapp = () => {
    if (!orderDetailId) return;
    sendOrderNotificationHook.sendOrderNotification(orderDetailId);
    handleMenuClose();
  };

  const onClickedShowHistory = () => {
    orderDetailContext.setShowHistoryDialog?.(true);
    handleMenuClose();
  };

  return {
    uiState: { ...uiState, openMenu },
    actions: {
      handleMenuClick,
      handleMenuClose,
      onClickedExportOrder,
      onClickedAcceptOrder,
      onClickedMarkAsDelivered,
      onClickedCancelOrder,
      onClickedSendWhatsapp,
      onClickedShowHistory,
    },
    references: {
      anchorEl,
    },
  };
}
